// Pedidos de venta: Fabric como base + overlay de Dataverse.
//
// Fabric va ~15 min por detrás de F&O, así que un pedido recién creado no
// aparecería en la lista durante ese rato. El overlay lo tapa leyendo en vivo
// lo creado en las últimas horas y superponiéndolo a las filas de Fabric.
//
// Las dos consultas van en paralelo y son independientes: la tabla pinta en
// cuanto responde Fabric, y si el overlay falla la página sigue funcionando.
import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { pedidosVentasApi } from '@/services/api'
import { mergePedidos } from '@/utils/mergePedidosVentas'
import type { PedidoVentaRow } from '@/types'

interface Opciones {
  /** Por defecto `true`. La pantalla de detalle lo apaga cuando ya tiene la
   *  cabecera por otra vía: traerse la lista entera para leer una fila que ya
   *  está en la mano sería tirar una consulta grande a la basura. */
  enabled?: boolean
}

export function usePedidosVentas(desde: string, hasta: string, { enabled = true }: Opciones = {}) {
  const qc = useQueryClient()

  const fabric = useQuery({
    queryKey: ['pedidos-ventas', desde, hasta],
    queryFn: () => pedidosVentasApi.getPedidos({ desde, hasta }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled,
  })

  const overlay = useQuery({
    queryKey: ['pedidos-ventas-recientes'],
    queryFn: pedidosVentasApi.getRecientes,
    // Corto: es la fuente de lo recién creado y debe envejecer rápido.
    staleTime: 30 * 1000,
    // Un fallo del overlay no debe insistir: la página funciona sin él.
    retry: 1,
    enabled,
  })

  const rows = useMemo(
    () => mergePedidos(
      (fabric.data?.data ?? []) as PedidoVentaRow[],
      (overlay.data?.data ?? []) as PedidoVentaRow[],
    ),
    [fabric.data, overlay.data],
  )

  /** Tras crear o editar un pedido hay que refrescar ambas fuentes: el overlay
   *  lo verá enseguida, Fabric dentro de un rato. */
  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['pedidos-ventas'] })
    qc.invalidateQueries({ queryKey: ['pedidos-ventas-recientes'] })
  }

  return {
    rows,
    isLoading: fabric.isLoading,
    isError: fabric.isError,
    isFetching: fabric.isFetching || overlay.isFetching,
    refetch: () => { fabric.refetch(); overlay.refetch() },
    invalidar,
  }
}
